<template>
  <div :key="rerenderKey" ref="videoContainer" class="player-container" @mouseover="videoHoveringState('true')" @mouseleave="videoHoveringState('false')" :class="[{'timelineVisible': timelineVisible && options.readOnly}, {'timelineInvisible': !timelineVisible && timeoutOver && options.readOnly}]">
    <!-- Show placeholder when video is not active -->
    <div v-if="!isVideoActive" class="video-placeholder" @click="activateAndPlayVideo">
      <div class="placeholder-content">
        <div class="play-icon">
        </div>
      </div>
    </div>
    
    <!-- This video element stays in the DOM but gets hidden -->
    <div class="video-container" :class="{'video-hidden': !isVideoActive}">
      <video ref="videoPlayer"  oncontextmenu="return false;" playsinline fluid="true" class="video-js vjs-default-skin" data-setup='{ "inactivityTimeout": 0 }' controlsList="nodownload"></video>
      <Bubbles 
      v-if="loaded && isVideoActive" 
      :key="options.videoTests.length" 
      :videoPlayer="player" 
      :timeframes="options.videoTests.map(el=>{ return {video_time: (options.start_time && options.readOnly ? el.video_time - options.start_time : el.video_time), 'is_triggered': el.is_triggered}})" 
      />
      <transition name="slide" :duration="1000">
        <Subtitles v-if="subtitlesWindowOpen" :subtitleList="[subtitleList]" :player="player" :captionsTr-toggled="captionsToggled"/>
      </transition>
    </div>
    <video v-if="showDefaultPlayer" oncontextmenu="return false;" playsinale covntrols="" controlsList="nodownload">
        <source :src="options.sources && options.sources[0] ? options.sources[0].src : (src || '')" type="video/m1">
        Your browser does not support the video tag.
    </video>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import youtube from'videojs-youtube';
import offset from 'videojs-offset';
videojs.registerPlugin("offset", offset);
import Bubbles from './Bubbles.vue';
import Subtitles from './Subtitles.vue';
const INJECTED_WATERMARK_CLASS = 'vjs-injected-watermark'; // Class name for easy selection

// Utility function to detect Android version
function getAndroidVersion() {
  const userAgent = navigator.userAgent;
  const match = userAgent.match(/Android ([0-9\.]+)/);
  return match ? parseFloat(match[1]) : null;
}

// Check if device is older Android (Android 10 or below)
function isOlderAndroid() {
  const androidVersion = getAndroidVersion();
  return androidVersion !== null && androidVersion <= 10.0;
  // return true;
}

// Create a global video manager to track loaded videos
if (!window.videoManager) {
  window.videoManager = {
    activeVideos: [],
    playingVideos: new Set(), // Track currently playing videos
    maxVideos: 3,
    singleVideoMode: isOlderAndroid(), // Enable single video mode for older Android
    
    registerVideo(id) {
      if (this.activeVideos.includes(id)) return true;
      
      if (this.activeVideos.length >= this.maxVideos) {
        return false;
      }
      
      this.activeVideos.push(id);
      // console.log('Registered video:', id, 'Active videos:', this.activeVideos);
      return true;
    },
    
    unregisterVideo(id) {
      this.activeVideos = this.activeVideos.filter(videoId => videoId !== id);
      this.playingVideos.delete(id);
      // console.log('Unregistered video:', id, 'Active videos:', this.activeVideos);
    },
    
    isRegistered(id) {
      return this.activeVideos.includes(id);
    },
    
    // New methods for managing playing videos
    startPlaying(id, playerInstance, componentInstance) {
      if (this.singleVideoMode) {
        // In single video mode, fully dispose other video players to free up resources
        this.playingVideos.forEach(videoId => {
          if (videoId !== id) {
            const otherComponent = this.getComponentById(videoId);
            if (otherComponent) {
              // Fully dispose the player instance to free up resources on older devices
              otherComponent.disposePlayerCompletely();
            }
          }
        });
        // Clear the set and add only the new playing video
        this.playingVideos.clear();
      }
      
      this.playingVideos.add(id);
      this.playerInstances = this.playerInstances || new Map();
      this.playerInstances.set(id, playerInstance);
      
      // Also store component instance reference
      this.componentInstances = this.componentInstances || new Map();
      this.componentInstances.set(id, componentInstance);
    },
    
    stopPlaying(id) {
      this.playingVideos.delete(id);
    },
    
    getPlayerById(id) {
      return this.playerInstances ? this.playerInstances.get(id) : null;
    },
    
    getComponentById(id) {
      return this.componentInstances ? this.componentInstances.get(id) : null;
    }
  };
}

export default {
  name: 'VideoPlayer',
  components:{
    Bubbles,
    Subtitles,
  },
  props: {
    options: {
      type: Object,
      default() {
        return {
        };
      }
    },
    src: {
      type: String,
    },
    id: {
      type: String,
      required: true
    },
    forceVisible: {
      type: Boolean,
      default: false
    },
    watermark:{
      type: Object
    }
  },
  data() {
    return {
      player: null,
      bubblesEL: null,
      loaded: false,
      isVideoActive: false,
      bubbleUpdates: 0,
      timeSet: false,
      captionsToggled: true,
      subtitlesWindowOpen: false,
      timelineVisible: false,
      subtitleList: [],
      timeoutOver: false,
      timer: '',
      showDefaultPlayer: false,
      observer: null,
      visibilityObserver: null,
      rerenderKey: 0,
      ccButton: null,
      initializationAttempted: false,
      isOnScreen: false,
      videoSrc: null,
      playerInitialized: false,
      isFullScreen: false,
      videoRenderIndex: 0,
      shouldAutoPlay: false,
      savedProgress: 0, // Save video progress time
    }
  },
  computed: {
    effectiveSrc() {
      return this.videoSrc || this.getVideoSrc();
    }
  },
  watch:{
    'options.videoTests':{
        handler(newVal, oldVal){
            this.bubbleUpdates++;
        },
        deep: true
    },
    'options.isVideoSettingsOpen':{
      handler(newVal,oldVal){
        if(newVal == true){
          if (this.player) this.player.addClass('vjs-sound-control-off');
        } else{
          if (this.player) this.player.removeClass('vjs-sound-control-off');
        }
      }
    },
    'options.ytVideoOnTabOne':{
      handler(newVal,oldVal){
        if(newVal == true){
          if (this.player) this.player.addClass('vjs-youtube-vid-first');
        } else{
          if (this.player) this.player.removeClass('vjs-youtube-vid-first');
        }
      }
    },
    'captionsToggled':{
      handler(newVal){
        if(newVal == true){
          $('.subtitles-button').addClass('active')
        }else{
          $('.subtitles-button').removeClass('active')
        }
      }
    },
    'watermark.src':{
      handler(newVal){
        this.recreateInjectedWatermark();
      }
    },
    'src': {
      handler(newSrc) {
        this.videoSrc = newSrc;
        if (this.isVideoActive && this.playerInitialized) {
          this.updateVideoSource(newSrc);
        }
      }
    },
    'forceVisible': {
      handler(newVal) {
        if (newVal && !this.isVideoActive) {
          this.makeVisible();
        }
      },
      immediate: true
    },
    'isVideoActive': {
      handler(newVal) {
        if(this.isFullScreen) return
        if (newVal) {
          // Initialize player if it's not already done
          
          if (!this.player) {
              this.initPlayer(this.effectiveSrc);
          } else {
            // If player exists, just ensure it's properly configured
              this.player.show();
              
              this.updatePlayerState();
          }
        } else if (this.player) {
          
          // Just clean up the VideoJS instance but keep the video element
          this.cleanupPlayer();
        }
      }
    },
    'isOnScreen': {
      handler(newVal) {
        if(this.isFullScreen) return
        
        // On older devices (single video mode), don't auto-activate on scroll
        if (window.videoManager.singleVideoMode) {
          // In single video mode, don't auto-activate or deactivate based on scroll
          return;
        }
        
        if (newVal && !this.isVideoActive && !this.forceVisible) {
          // Auto-initialize if the video is on screen (only on newer devices)
          this.makeVisible();
        
        } else if (!newVal && this.isVideoActive && !this.forceVisible) {
          // Unload if scrolled off screen and not forced visible (only on newer devices)
          this.deactivateVideo();
        }
      }
    }
  },
  mounted() {
    this.videoSrc = this.src;
    this.videoRenderIndex++;
    this.$emit('setVideoKey', this.videoRenderIndex);
    this.initVisibilityObserver();
            
    // If we're in the initial viewable area or forceVisible is true, make the video visible
    if (this.forceVisible) {
      this.makeVisible();
    }
  },
  beforeDestroy() {
    if (this.player) {
      this.removeInjectedWatermark();
      this.disposePlayer();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    
    window.videoManager.unregisterVideo(this.id);
  },
  methods: {
    getVideoTitle() {
      // Extract title from source or use a default
      if (this.options.title) {
        return this.options.title;
      }
      
      if (this.options.sources && this.options.sources[0]) {
        const src = this.options.sources[0].src;
        // Extract file name from path
        const fileName = src.split('/').pop().split('?')[0];
        // Remove extension
        return fileName.replace(/\.[^/.]+$/, "");
      }
      
      return "Video";
    },
    getVideoSrc() {
      // Get the video source from props or options
      if (this.src) {
        return this.src;
      }
      
      if (this.options.sources && this.options.sources[0]) {
        return this.options.sources[0].src;
      }
      
      return null;
    },
    makeVisible() {
      // console.log('Making video visible:', this.id);
      this.isVideoActive = true;
      
      // Register this video with the video manager
      window.videoManager.registerVideo(this.id);
      
      // Also store component instance reference for single video mode
      if (!window.videoManager.componentInstances) {
        window.videoManager.componentInstances = new Map();
      }
      window.videoManager.componentInstances.set(this.id, this);
    },
    updatePlayerState() {
      // If player exists but is hidden, show it
      if (this.player && this.$refs.videoPlayer) {
        // Re-enable controls if they were disabled
        if (this.player.controls && typeof this.player.controls === 'function') {
          this.player.controls(true);
        }
        
        // Re-set sources and other settings if needed
        if (!this.player.src()) {
          this.updateVideoSource(this.effectiveSrc);
        }
      }
    },
    deactivateVideo() {
      // Clear auto-play flag
      this.shouldAutoPlay = false;
      
      // Just clean up the VideoJS instance but keep the video element
      if (this.player) {
        this.cleanupPlayer();
      }
      
      // Then update the state and unregister
      this.isVideoActive = false;
      window.videoManager.unregisterVideo(this.id);
    },
    updateVideoSource(newSrc) {
      if (this.player) {
        if(window.ReactNativeWebView)
          window.ReactNativeWebView.postMessage("changingVideoSrc:" + newSrc);
        
        try {
          let videoOptions = {...this.options};
          if (newSrc && (!videoOptions.sources || !videoOptions.sources.length)) {
            videoOptions.sources = [{ type: 'video/mp4', src: newSrc }];
          }
          
          // Update the source and load the new video
          this.player.src(videoOptions.sources);
          this.player.load();
          
          // Reset any errors
          if (this.player.error) {
            this.player.error(null);
          }
        } catch (e) {
          console.error('Error updating video source:', e);
          
          // If updating source fails, fully recreate the player
          this.disposePlayer();
          this.$nextTick(() => {
            this.initPlayer(newSrc);
          });
        }
      } else {
        if(window.ReactNativeWebView)
          window.ReactNativeWebView.postMessage("changingVideoSrc1:" + newSrc);
        
        // Initialize player if it doesn't exist
        this.initPlayer(newSrc);
      }
    },
    initVisibilityObserver() {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const isVisible = entry.isIntersecting;
          
          this.isOnScreen = isVisible;
          
          // If the video is coming into view and is forced visible, make it visible
          if (isVisible && !this.isVideoActive && this.forceVisible) {
            this.makeVisible();
          }
          
          // On older devices (single video mode), don't auto-initialize videos
          // Let users explicitly click the placeholder to activate videos
          if (window.videoManager.singleVideoMode) {
            // In single video mode, only activate if forced visible
            return;
          }
          
          // If there are active videos less than max, and this video becomes visible
          // auto-initialize it (only on newer devices)
          if (isVisible && !this.isVideoActive && !this.forceVisible && 
              window.videoManager.activeVideos.length < window.videoManager.maxVideos) {
            this.makeVisible();
          }
        },
        {
          threshold: 0.3, // Element is at least 30% visible
          rootMargin: '100px' // Start loading slightly before it comes into view
        }
      );
      
      this.visibilityObserver.observe(this.$refs.videoContainer);
    },
    initPlayer(src = null) {
      if (!this.$refs.videoPlayer) {
        console.error('Video player element not found');
        return;
      }
      
      // If the video element already has a VideoJS instance, clean it up
      if (this.$refs.videoPlayer.player) {
        try {
          videojs(this.$refs.videoPlayer).dispose();
        } catch (e) {
          console.warn('Error disposing existing player:', e);
        }
      }
      
      let vm = this;
      let videoSrc = src || this.getVideoSrc();
      
      if (!videoSrc) {
        console.error('No video source provided');
        return;
      }
      
      let videoOptions = {...this.options};
      
      
      if (videoSrc && (!videoOptions.sources || !videoOptions.sources.length)) {
        videoOptions.sources = [{ type: 'video/mp4', src: videoSrc }];
      }
      
      try {
        // console.log('Initializing player for ID:', this.id);
        // Make sure the element is visible before initializing
        if (this.$refs.videoPlayer.parentElement) {
          this.$refs.videoPlayer.parentElement.style.display = 'block';
          this.$refs.videoPlayer.style.display = 'block';
        }
        
        // Use videojs to initialize the player
        this.player = videojs(this.$refs.videoPlayer, videoOptions, function() {
          // console.log('Player ready for:', vm.id);
          vm.playerInitialized = true;
          vm.$emit('loaded', vm.player);
          if(vm.watermark){
            vm.injectWatermark();
          }
          this.on('loadedmetadata', function() {
            vm.$emit('loadedmetadata', vm.player);
            vm.loaded = true;
            
            // Restore saved video progress when metadata is loaded
            vm.restoreVideoProgress();
            
            // If auto-play was requested, attempt it now that metadata is loaded
            if (vm.shouldAutoPlay) {
              vm.attemptAutoPlay();
            }
          });
          
          if(vm.options.readOnly && !vm.options.allowSeeking)
            this.controlBar.progressControl.disable();
            
          if (vm.options.type == 'yt' && vm.options.readOnly) {
            if (!vm.ccButton) {
              vm.ccButton = this.controlBar.addChild('button', {}, 15);
              vm.ccButton.addClass('subtitles-button');
              if (vm.captionsToggled) {
                vm.ccButton.addClass('active');
              }
              vm.ccButton.controlText(vm.toLocal ? vm.toLocal('generic.subtitles') : 'Subtitles');
              vm.ccButton.el().onclick = vm.loadSubs;
            }
          }
          
          this.on('fullscreenchange', () => {
            if (vm.isIOS()) {
              if (vm.player.isFullscreen()) {
                if (vm.$store && vm.$store.state) {
                  vm.$store.state.fullscreenedVideo = vm.options.item_id;
                }
              } else {
                if (vm.$store && vm.$store.state) {
                  vm.$store.state.fullscreenedVideo = null;
                }
                setTimeout(() => {
                  if (vm.$refs.videoContainer) {
                    vm.$refs.videoContainer.scrollIntoView();
                  }
                },10)
              }
            }
            vm.isFullScreen = vm.player.isFullscreen()
            

          });
           
          this.on('ended', function() {
            console.log('Video ended');
            vm.$emit('videoEnded');
            setTimeout(() => {
              if (vm.player && vm.player.controlBar && vm.player.controlBar.playToggle) {
                vm.player.controlBar.playToggle.el().classList.add('vjs-ended');
              }
            }, 200);
          });
          
          this.on('play', function() {
            if (vm.player && vm.player.controlBar && vm.player.controlBar.playToggle) {
              vm.player.controlBar.playToggle.el().classList.remove('vjs-ended');
            }
            
            // Notify video manager that this video is playing
            window.videoManager.startPlaying(vm.id, vm.player, vm);
          });
          
          this.on('pause', function() {
            // Notify video manager that this video stopped playing
            window.videoManager.stopPlaying(vm.id);
          });

          this.on('seeking', function() {
            if (vm.player && vm.player.controlBar && vm.player.controlBar.playToggle) {
              vm.player.controlBar.playToggle.el().classList.remove('vjs-ended');
            }
          });

          this.on('seeked', function() {
            if (vm.player && vm.player.controlBar && vm.player.controlBar.playToggle) {
              vm.player.controlBar.playToggle.el().classList.remove('vjs-ended');
            }
          });
          
          this.on('error', function(e) {
            e.stopImmediatePropagation();
            console.error('Video player error:', this.error());
            vm.cleanupPlayer();
            vm.showDefaultPlayer = true;
          });
         
        });
   
      
        this.initIntersectionObserver();
        this.player.addClass('vjs-matrix');
        
        if(this.options.isVideoSettingsOpen && !this.options.readOnly){
          this.player.addClass('vjs-sound-control-off');
        }
        
        if(this.options.type == 'yt'){
          this.player.addClass('vjs-youtube-vid-first');
        }
      } catch (error) {
        console.error('Error initializing video player:', error);
        this.showDefaultPlayer = true;
      }
    },
    initIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && this.player) {
              this.player.pause();
            }
          });
        },
        {
          threshold: [0.1, 0.5], // Check at different visibility levels
        }
      );
      
      if (this.$refs.videoContainer) {
        this.observer.observe(this.$refs.videoContainer);
      }
    },
    cleanupPlayer() {
      // This method cleans up the VideoJS instance but keeps the video element
      if (this.player) {
        try {
          // First pause the video
          this.player.pause();
          
          // Remove sources (this helps free up resources)
          // this.player.src("");
          
          
          // Reset UI elements
          // if (this.player.controlBar && this.player.controlBar.playToggle) {
          //   this.player.controlBar.playToggle.el().classList.remove('vjs-ended');
          // }
          
          // Remove event listeners
          // this.player.off('play');
          // this.player.off('pause');
          // this.player.off('seeking');
          // this.player.off('seeked');
          // this.player.off('ended');
          // this.player.off('loadedmetadata');
          // this.player.off('error');
          // this.player.off('fullscreenchange');
          
          // Hide but don't dispose
          this.player.hide();
          // this.player.dispose();

          // Don't reset player reference - we'll reuse it when reactivated
        } catch (e) {
          console.error('Error cleaning up player:', e);
          
          // If cleanup fails, fully dispose
          this.disposePlayer();
          return;
        }
        
        // Reset state but keep playerInitialized true since we're not disposing the player
        this.loaded = false;
        this.ccButton = null;
        // Don't reset playerInitialized - the player is still valid for reuse
      }
    },
    disposePlayer() {
      // This method completely disposes of the VideoJS instance
      if (this.player) {
        try {
          // Save current progress before disposing
          this.saveVideoProgress();
          
          // First pause the video
          this.player.pause();
          
          // Then dispose it properly
          this.player.dispose();
        } catch (e) {
          console.error('Error disposing player:', e);
        }
        
        // Clear references
        this.player = null;
        this.loaded = false;
        this.ccButton = null;
        this.playerInitialized = false;
      }
    },
    disposePlayerCompletely() {
      // Method specifically for single video mode - completely dispose and deactivate
      this.shouldAutoPlay = false;
      
      // Fully dispose the player instance
      this.disposePlayer();
      
      // Reset the video element to ensure clean reinitialization
      if (this.$refs.videoPlayer) {
        // Clear any VideoJS data attributes
        this.$refs.videoPlayer.removeAttribute('data-vjs-player');
        this.$refs.videoPlayer.className = 'video-js vjs-default-skin';
        
        // Force re-render of the video element by updating a key
        this.rerenderKey++;
      }
      
      // Deactivate the video component
      this.isVideoActive = false;
      window.videoManager.unregisterVideo(this.id);
    },
    videoHoveringState(state){
      if(this.options.readOnly){
        if(state == 'true'){
          clearTimeout(this.timer);
          this.timelineVisible = true;
          this.timeoutOver = false;
        }else{
          this.timelineVisible = false;
          // this.startTimeout();
        }
      }
    },
    loadSubs(){
      this.subtitlesWindowOpen = !this.subtitlesWindowOpen;
      if(this.player && this.player.tech_ && this.player.tech_.ytPlayer && this.player.tech_.ytPlayer.getOption){
        if(this.player.tech_.ytPlayer.getOption('captions', 'tracklist')){
          this.subtitleList = this.player.tech_.ytPlayer.getOption('captions', 'tracklist');
        }
      }
    },
    toggleSubtitles(){
      if(this.options.type=='yt' && this.player && this.player.tech_ && this.player.tech_.ytPlayer){
        if(this.subtitleList.length == 0) return;
        if(this.captionsToggled){
          this.player.tech_.ytPlayer.unloadModule('captions')
          this.player.tech_.ytPlayer.unloadModule('cc')
          this.currentLanguage = 'en';
          this.captionsToggled = false;
        }else{
          this.player.tech_.ytPlayer.loadModule('captions')
          this.player.tech_.ytPlayer.loadModule('cc')
          this.player.tech_.ytPlayer.setOption("captions", "track", {"languageCode": this.currentLanguage});
          this.captionsToggled = true;
        }
      }
    },
    isIOS() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    },
    toLocal(key) {
      // If you have a localization function, use it here
      // Otherwise return a fallback string
      return key.split('.').pop();
    },
    injectWatermark() {
      // Prevent duplicate injection if called accidentally
      this.removeInjectedWatermark();

      if (!this.player || (!this.watermark.src && !this.watermark.text)) {
        return; // No player or no watermark content
      }

      const playerEl = this.player.el(); // Get the main player DOM element (div.video-js)
      if (!playerEl) return;

      // 1. Create the watermark container element
      const watermarkEl = document.createElement('div');
      watermarkEl.className = INJECTED_WATERMARK_CLASS; // Add our class

      // 2. Create the content (Image or Text)
      if (this.watermark.src) {
        const img = document.createElement('img');
        img.src = this.watermark.src;
        img.alt = 'Watermark';
        img.style.display = 'block'; // Prevent extra space
        img.style.maxWidth = this.watermark.maxWidth+'px'; // Adjust as needed, or use CSS
        img.style.height = 'auto';
        watermarkEl.appendChild(img);
      } else if (this.watermark.text) {
        const span = document.createElement('span');
        span.textContent = this.watermark.text;
        // Apply basic text styles (or use CSS)
        span.style.color = 'white';
        span.style.backgroundColor = 'rgba(0, 0, 0, 0.4)';
        span.style.padding = '4px 8px';
        span.style.fontSize = '14px';
        span.style.borderRadius = '3px';
        span.style.whiteSpace = 'nowrap';
        watermarkEl.appendChild(span);
      }

      // 3. Apply common and positional styles
      watermarkEl.style.position = 'absolute';
      watermarkEl.style.zIndex = '10'; // May need to be higher than controls depending on theme
      watermarkEl.style.pointerEvents = 'none'; // VERY important
      watermarkEl.style.userSelect = 'none';
      watermarkEl.style.webkitUserDrag = 'none';
      watermarkEl.style.opacity = this.watermark.opacity.toString();
      watermarkEl.style.transition = 'opacity 0.3s ease'; // Optional fade

      // Calculate positions
      const x = `${this.watermark.XOffset}px`;
      const y = `${this.watermark.YOffset}px`;
      watermarkEl.style.top = 'auto';
      watermarkEl.style.right = 'auto';
      watermarkEl.style.bottom = 'auto';
      watermarkEl.style.left = 'auto';
      watermarkEl.style.transform = ''; // Reset

      switch (this.watermark.position) {
        case 'top-left':    watermarkEl.style.top = y; watermarkEl.style.left = x; break;
        case 'bottom-right': watermarkEl.style.bottom = y; watermarkEl.style.right = x; break;
        case 'bottom-left': watermarkEl.style.bottom = y; watermarkEl.style.left = x; break;
        case 'center':
          watermarkEl.style.top = '50%';
          watermarkEl.style.left = '50%';
          watermarkEl.style.transform = 'translate(-50%, -50%)';
          break;
        case 'top-right':
        default:            watermarkEl.style.top = y; watermarkEl.style.right = x; break;
      }

      // 4. Append the styled watermark to the player element
      playerEl.appendChild(watermarkEl);
      console.log('Watermark injected into player.');
    },

    removeInjectedWatermark() {
      if (!this.player) return;
      const playerEl = this.player.el();
      const existingWatermark = playerEl?.querySelector(`.${INJECTED_WATERMARK_CLASS}`);
      if (existingWatermark) {
        playerEl.removeChild(existingWatermark);
      }
    },

    recreateInjectedWatermark() {
        // This is called by watchers when props change
        if (this.player) {
            this.injectWatermark();
        }
    },
    
    activateAndPlayVideo() {
      // In single video mode, immediately dispose other video players to free up resources
      if (window.videoManager.singleVideoMode) {
        // Fully dispose all other active videos completely (both playing and just rendered)
        const allActiveVideos = [...window.videoManager.activeVideos, ...window.videoManager.playingVideos];
        allActiveVideos.forEach(videoId => {
          if (videoId !== this.id) {
            const otherComponent = window.videoManager.getComponentById(videoId);
            if (otherComponent) {
              // Fully dispose the player instance to free up resources on older devices
              otherComponent.disposePlayerCompletely();
            }
          }
        });
        // Clear both sets
        window.videoManager.playingVideos.clear();
        window.videoManager.activeVideos = [];
      }
      
      // Activate this video (will render it)
      this.makeVisible();
      
      // Set a flag to auto-play when ready
      this.shouldAutoPlay = true;
      
      // Try to play immediately if already ready
      this.$nextTick(() => {
        this.attemptAutoPlay();
      });
    },
    
    attemptAutoPlay() {
      if (!this.shouldAutoPlay) return;
      
      if (this.player && this.playerInitialized) {
        // Check if player already has metadata loaded (readyState >= 1)
        const hasMetadata = this.player.readyState && this.player.readyState() >= 1;
        
        if (this.loaded || hasMetadata) {
          // Player is fully ready, play it
          try {
            this.player.play().then(() => {
              this.shouldAutoPlay = false;
            }).catch((error) => {
              console.log('Auto-play prevented by browser:', error);
              this.shouldAutoPlay = false;
            });
          } catch (e) {
            console.log('Auto-play error:', e);
            this.shouldAutoPlay = false;
          }
        } else {
          // Player initialized but metadata not loaded yet, wait for loadedmetadata
          this.player.one('loadedmetadata', () => {
            if (this.shouldAutoPlay) {
              this.attemptAutoPlay();
            }
          });
        }
      } else {
        // Player not ready yet, check again in a moment
        setTimeout(() => {
          if (this.shouldAutoPlay) {
            this.attemptAutoPlay();
          }
        }, 100);
      }
    },
    
    saveVideoProgress() {
      // Save current playback time when video is disposed
      if (this.player && this.player.currentTime && this.player.readyState() >= 1) {
        const currentTime = this.player.currentTime();
        const duration = this.player.duration();
        
        // Only save progress if video has played for at least 5 seconds
        // and is not within the last 10 seconds (to avoid saving near completion)
        if (currentTime > 5 && duration > 0 && currentTime < (duration - 10)) {
          this.savedProgress = currentTime;
          console.log(`Saved progress for video ${this.id}: ${this.savedProgress}s`);
        }
      }
    },
    
    restoreVideoProgress() {
      // Restore saved playback time when video is ready
      if (this.player && this.player.currentTime && this.savedProgress > 0) {
        const vm = this;
        
        // Wait a bit for the video to be fully ready before seeking
        setTimeout(() => {
          if (vm.player && vm.player.readyState() >= 1) {
            try {
              vm.player.currentTime(vm.savedProgress);
              console.log(`Restored progress for video ${vm.id}: ${vm.savedProgress}s`);
            } catch (e) {
              console.log('Error restoring video progress:', e);
            }
          }
        }, 500);
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.subtitles-button{
  border: 1px solid red;
  position: absolute;
  bottom: 30px;
  background-color: white;
}
.player-container{
    display: flex;
    width: 100%;
    max-width: 100%;
    position: absolute;
    top: 0;
    max-height: 100%;
    left: 0;
    height: 100%;
    .subtitles-container{
      opacity: 0;
      transition: 0.25s;
    }
    &:hover{
      .subtitles-container{
        opacity: 1;
      }
    }
}

.video-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  
  &.video-hidden {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
}

.video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  cursor: pointer;
  z-index: 10;
  position: absolute;
  top: 0;
  left: 0;
  
  .placeholder-content {
    text-align: center;
    
    .play-icon {
       background-image: url('../../../assets/images/icons/play-button.png');
      background-repeat: no-repeat;
      background-position: center;
      background-color: transparent;
      border: none;
      height: 50px;
      width: 50px;
      &:before {
        content: "";
        display: none;
      }
      &:hover {
        background-color: transparent;
        opacity: .7;
      }
    }
    
    .placeholder-text {
      margin-top: 10px;
      color: #555;
      font-size: 16px;
      background-color: rgba(255, 255, 255, 0.8);
      padding: 5px 10px;
      border-radius: 4px;
    }
    
    .placeholder-title {
      margin-top: 5px;
      color: #777;
      font-size: 14px;
      max-width: 80%;
      margin-left: auto;
      margin-right: auto;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      background-color: rgba(255, 255, 255, 0.8);
      padding: 3px 8px;
      border-radius: 3px;
    }
  }
  
 
}
</style>
<style lang="scss">
.player-container{
    .video-js{
        height: 100%;
        max-height: 100%;
        width: 100%;
        position: absolute;
        left: 0;
        z-index: auto;
        iframe{
           touch-action: manipulation;
  pointer-events: auto;
        }
    }
    .vjs-youtube-vid-first .vjs-control-bar{
          opacity: 0.0;
          transition: 0.25s;
        }
        &:hover{
          .vjs-youtube-vid-first .vjs-control-bar{
            opacity: 1;
          }
    }
}
.vjs-custom-timestamp-bubbles {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 30px;
  width: 30px;
  background: red;
  pointer-events: none;
}

.vjs-matrix.video-js {
  .vjs-slider-bar{
    color: var(--secondary-color);
    background-color: var(--secondary-color);
    z-index: 2;
    &::before{
      z-index: 2;
    }
  }
}
.timelineVisible{
  .vjs-control-bar{
    opacity: 1;
  }
}
.timelineInvisible{
  .vjs-control-bar{
    opacity: 0;
  }
}
.video-js{
  .vjs-control-bar{
  height: 4em;
    .subtitles-button{
      &::before{
        transition: 0.25s;
        border-radius: 3px;
        border: 1px solid white;
        padding: 2px;
        content: "CC";
        cursor: pointer;
      }
      &.active{
        &::before{
          background-color: white;
          color: #333333;
        }
      }
    }
}
}
.video-js .vjs-control{
  height: 80%;
}
.vjs-sound-control-off .vjs-subs-caps-button, .vjs-sound-control-off .vjs-playback-rate, .vjs-sound-control-off .vjs-volume-panel{
  display: none;
}
.vjs-sound-control-off .vjs-playback-rate{
  display: none;
}

.video-js .vjs-progress-control .vjs-mouse-display{
  bottom: 0px;
}
.video-js .vjs-time-tooltip{
  top: -50px;
}
.ios-fullscreen {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: black;
}

ul {
   cursor: pointer;
}
.testas{
  position: absolute;
  top: 10px;
  left: 0;
  color:red;
  z-index: 9999;
}
.slide-enter-active {
   -moz-transition-duration: 0.3s;
   -webkit-transition-duration: 0.3s;
   -o-transition-duration: 0.3s;
   transition-duration: 0.3s;
   -moz-transition-timing-function: ease-in;
   -webkit-transition-timing-function: ease-in;
   -o-transition-timing-function: ease-in;
   transition-timing-function: ease-in;
}

.slide-leave-active {
   -moz-transition-duration: 0.3s;
   -webkit-transition-duration: 0.3s;
   -o-transition-duration: 0.3s;
   transition-duration: 0.3s;
   -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
   -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
   -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
   transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.slide-enter-to, .slide-leave {
   max-height: 150px;
   overflow: hidden;
}

.slide-enter, .slide-leave-to {
   overflow: hidden;
   max-height: 0;
}
.vjs-fullscreen .vjs-progress-control { width: 86% !important; }

/* Fix for vertical videos in fluid mode */
.video-js.vjs-fluid {
  padding-top: 0 !important;
  height: 100% !important;
}

.video-js.vjs-fluid:not(.vjs-audio-only-mode) {
  padding-top: 0 !important;
  height: 100% !important;
}

/* Override VideoJS dynamic dimensions for vertical videos */
.video-js[class*="vjs_video_"]:not(.vjs-audio-only-mode) {
  padding-top: 0 !important;
  height: 100% !important;
}

/* Ensure video element maintains aspect ratio within bounds */
.video-js .vjs-tech {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
}
</style>